/*
 * The services step's reads, started as soon as the location is known
 * (`BookingFlowWrapper`), while the step's own code is still loading: the
 * dog and cat menus (the step's dog/cat check reads both, and the menu is
 * one of them), the breeds, the salon's booking site menu (phase 8), Popular
 * (phase 9c) for the pet the step opens on, and signed in: the account's
 * pets, the client's past visits here (AGAIN and the ↻ tags: the last-visit
 * read for the location) and the photos of the pets this phone already knows.
 *
 * Every read here is the step's own, shared through its cache or the read
 * under way, so nothing is read twice; a failure here is the step's to show.
 * Before, each of them started only once the step had mounted, and the
 * photos only once the pets had arrived.
 */
import { optimizedApiService } from '../config/api.config';
import { getCMSPets } from './auth.service';
import { bookingStateService } from './bookingState.service';
import { readVisits } from './lastVisits.service';
import { prefetchPetPhotos } from './petPhoto.service';
import { petsStore } from './pets.store';

/** The photos started ahead: the pet card's and the first chips on a phone's screen. */
const PHOTOS_AHEAD = 6;

/** The pets the step will most likely open with: the saved booking's, else the account's as last read here. */
const petsLikelyShown = (): number[] => {
  const ids = (list: unknown): number[] =>
    (Array.isArray(list) ? list : []).map(p => Number((p as { id?: unknown })?.id)).filter(id => Number.isInteger(id) && id > 0);
  const read = (from: () => unknown): number[] => {
    try {
      return ids(from());
    } catch {
      return [];
    }
  };
  const saved = read(() => bookingStateService.getBookingState()?.selectedPets);
  return (saved.length > 0 ? saved : read(getCMSPets)).slice(0, PHOTOS_AHEAD);
};

/**
 * Popular's read for the list the step opens on (phase 9c, G29): signed out, the
 * menu it browses first (?category=cat, else dogs) with no breed; signed in, the
 * saved booking's first pet, with its breed for "#2 for dogs like Leo". With no
 * saved pet the step starts it once the pets arrive.
 */
const popularAhead = (signedIn: boolean): { petType: 1 | 2; breedName: string | null } | null => {
  if (!signedIn) {
    const category = new URLSearchParams(window.location.search).get('category');
    return { petType: category === 'cat' ? 1 : 2, breedName: null };
  }
  try {
    const first = bookingStateService.getBookingState()?.selectedPets?.[0];
    if (!first) return null;
    return { petType: first.type === 'cat' ? 1 : 2, breedName: first.breed?.trim() || null };
  } catch {
    return null;
  }
};

export const warmServicesStep = (storeId: number | undefined, signedIn: boolean): void => {
  const quiet = () => undefined;
  if (storeId) optimizedApiService.checkCategoryAvailability(Number(storeId)).catch(quiet);
  optimizedApiService.fetchBreeds().catch(quiet);
  optimizedApiService.fetchBreedGroups().catch(quiet);
  // The salon's own pills (phase 8, G28); a failure is the step's to settle on today's bar.
  optimizedApiService.fetchBookingSiteMenu().catch(quiet);
  // Popular (phase 9c); a failure is the step's to show as no Popular.
  const ahead = storeId ? popularAhead(signedIn) : null;
  if (storeId && ahead) optimizedApiService.fetchPopular({ storeId: Number(storeId), ...ahead }).catch(quiet);
  if (!signedIn) return;
  petsStore.settleRecent().catch(quiet);
  void readVisits(storeId);
  prefetchPetPhotos(petsLikelyShown());
};
